import { useEffect, useState, useCallback, useMemo } from 'react'
import { useAuth } from '../context/AuthContext'
import {
  adminListUsers,
  adminCreateUser,
  adminUpdateUser,
  adminDeleteUser,
  type AdminUser,
} from '../api/client'
import { Users, Plus, Trash2, Edit, X, RefreshCw, ShieldAlert, AlertCircle, Loader2 } from 'lucide-react'

const ROLES = ['admin', 'editor', 'user', 'viewer'] as const

function generatePassword(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*'
  let pw = ''
  for (let i = 0; i < 24; i++) {
    pw += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return pw
}

function ModalOverlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-gray-950 border border-gray-800 rounded-xl shadow-xl text-gray-200"
        role="dialog"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  )
}

export default function AdminUsersPage() {
  const { role, username: currentUser } = useAuth()

  const [users, setUsers] = useState<AdminUser[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  // Create modal state
  const [createOpen, setCreateOpen] = useState(false)
  const [createUsername, setCreateUsername] = useState('')
  const [createPassword, setCreatePassword] = useState('')
  const [createRole, setCreateRole] = useState<string>('user')
  const [createAutoPw, setCreateAutoPw] = useState(true)
  const [creating, setCreating] = useState(false)
  const [createErr, setCreateErr] = useState<string | null>(null)

  // Edit modal state
  const [editTarget, setEditTarget] = useState<AdminUser | null>(null)
  const [editRole, setEditRole] = useState<string>('user')
  const [editPassword, setEditPassword] = useState('')
  const [editResetPw, setEditResetPw] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editErr, setEditErr] = useState<string | null>(null)

  // Delete confirm state
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteErr, setDeleteErr] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setErr(null)
    try {
      const data = await adminListUsers()
      setUsers(data)
    } catch {
      setErr('Failed to load users.')
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    void (async () => {
      try {
        const data = await adminListUsers()
        if (!cancelled) setUsers(data)
      } catch {
        if (!cancelled) setErr('Failed to load users.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // Auto-generate password for create modal
  useEffect(() => {
    if (createAutoPw) {
      setCreatePassword(generatePassword())
    }
  }, [createAutoPw, createOpen])

  const isAdmin = role === 'admin'

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => a.username.localeCompare(b.username))
  }, [users])

  // ----- Create -----
  const handleCreate = async () => {
    const trimmedUser = createUsername.trim()
    if (!trimmedUser) {
      setCreateErr('Username is required.')
      return
    }
    const pw = createAutoPw ? generatePassword() : createPassword
    setCreating(true)
    setCreateErr(null)
    try {
      await adminCreateUser({ username: trimmedUser, password: pw, role: createRole })
      setCreateOpen(false)
      setCreateUsername('')
      setCreatePassword('')
      setCreateRole('user')
      setCreateAutoPw(true)
      await refresh()
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error || 'Create failed.'
          : 'Create failed.'
      setCreateErr(msg)
    } finally {
      setCreating(false)
    }
  }

  const openCreate = () => {
    setCreateUsername('')
    setCreatePassword(generatePassword())
    setCreateRole('user')
    setCreateAutoPw(true)
    setCreateErr(null)
    setCreateOpen(true)
  }

  // ----- Edit -----
  const openEdit = (u: AdminUser) => {
    setEditTarget(u)
    setEditRole(u.role)
    setEditPassword('')
    setEditResetPw(false)
    setEditErr(null)
  }

  const handleEditSave = async () => {
    if (!editTarget) return
    const input: { role?: string; password?: string } = {}
    if (editRole !== editTarget.role) input.role = editRole
    if (editResetPw && editPassword) input.password = editPassword
    if (Object.keys(input).length === 0) {
      setEditErr('No changes to save.')
      return
    }
    setSaving(true)
    setEditErr(null)
    try {
      await adminUpdateUser(editTarget.username, input)
      setEditTarget(null)
      await refresh()
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error || 'Update failed.'
          : 'Update failed.'
      setEditErr(msg)
    } finally {
      setSaving(false)
    }
  }

  // ----- Delete -----
  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    setDeleteErr(null)
    try {
      await adminDeleteUser(deleteTarget.username)
      setDeleteTarget(null)
      await refresh()
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error || 'Delete failed.'
          : 'Delete failed.'
      setDeleteErr(msg)
    } finally {
      setDeleting(false)
    }
  }

  // ----- Access denied -----
  if (!isAdmin) {
    return (
      <div className="w-full max-w-[min(100%,72rem)] mx-auto px-4 lg:px-10 py-8 text-gray-200">
        <div className="flex items-center gap-3 text-amber-400 bg-amber-950/30 border border-amber-900/40 rounded-lg p-6">
          <ShieldAlert className="w-8 h-8 shrink-0" />
          <div>
            <h2 className="text-lg font-semibold">Access denied</h2>
            <p className="text-amber-300/70 text-sm mt-1">You need the admin role to manage users.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full max-w-[min(100%,72rem)] mx-auto px-4 lg:px-10 py-8 text-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-primary-400" />
          <div>
            <h1 className="text-2xl font-bold text-gray-100">User management</h1>
            <p className="text-gray-500 text-sm mt-0.5">{users.length} user{users.length === 1 ? '' : 's'}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 text-sm font-medium rounded-lg px-4 py-2 min-h-[44px] bg-primary-900/40 border border-primary-700/50 text-primary-100 hover:bg-primary-900/60"
        >
          <Plus className="w-4 h-4" />
          Create user
        </button>
      </div>

      {/* Error banner */}
      {err && (
        <div className="flex items-center gap-2 text-error-400 bg-red-950/30 border border-red-900/40 rounded-lg p-4 mb-4">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>{err}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Loading users…
        </div>
      )}

      {/* Users table */}
      {!loading && (
        <div className="rounded-lg border border-gray-800 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-900/80 text-left text-gray-400">
              <tr>
                <th className="p-3 font-medium">Username</th>
                <th className="p-3 font-medium">Role</th>
                <th className="p-3 font-medium">Created</th>
                <th className="p-3 font-medium">Last login</th>
                <th className="p-3 font-medium w-[120px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-6 text-center text-gray-500">No users found.</td>
                </tr>
              )}
              {sortedUsers.map((u) => (
                <tr key={u.id} className="border-t border-gray-800/80">
                  <td className="p-3 font-medium text-gray-100">{u.username}</td>
                  <td className="p-3">
                    <span
                      className={`inline-block text-xs font-medium px-2.5 py-0.5 rounded-full ${
                        u.role === 'admin'
                          ? 'bg-purple-900/50 text-purple-200 border border-purple-700/40'
                          : u.role === 'editor'
                          ? 'bg-blue-900/50 text-blue-200 border border-blue-700/40'
                          : u.role === 'viewer'
                          ? 'bg-gray-800 text-gray-400 border border-gray-700/40'
                          : 'bg-green-900/50 text-green-200 border border-green-700/40'
                      }`}
                    >
                      {u.role}
                    </span>
                  </td>
                  <td className="p-3 text-gray-400 whitespace-nowrap">
                    {u.created_at ? new Date(u.created_at).toLocaleString() : '—'}
                  </td>
                  <td className="p-3 text-gray-400 whitespace-nowrap">
                    {u.last_login ? new Date(u.last_login).toLocaleString() : '—'}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => openEdit(u)}
                        className="p-2 rounded-lg text-gray-400 hover:bg-gray-800 hover:text-gray-100"
                        title="Edit user"
                        aria-label={`Edit user ${u.username}`}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        disabled={u.username === currentUser}
                        onClick={() => {
                          setDeleteErr(null)
                          setDeleteTarget(u)
                        }}
                        className="p-2 rounded-lg text-gray-400 hover:bg-red-900/40 hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed"
                        title={u.username === currentUser ? 'Cannot delete yourself' : 'Delete user'}
                        aria-label={u.username === currentUser ? `Cannot delete ${u.username}` : `Delete user ${u.username}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Create user modal ── */}
      {createOpen && (
        <ModalOverlay onClose={() => setCreateOpen(false)}>
          <div className="p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-100">Create user</h2>
              <button
                type="button"
                onClick={() => setCreateOpen(false)}
                className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-800 hover:text-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {createErr && (
              <div className="text-sm text-error-400 bg-red-950/30 border border-red-900/40 rounded-lg px-4 py-3">
                {createErr}
              </div>
            )}

            <div className="space-y-4">
              <label className="flex flex-col gap-1.5 text-sm">
                <span className="text-gray-400">Username</span>
                <input
                  value={createUsername}
                  onChange={(e) => setCreateUsername(e.target.value)}
                  className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2.5 text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g. jdoe"
                  autoFocus
                />
              </label>

              <label className="flex flex-col gap-1.5 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Password</span>
                  <button
                    type="button"
                    onClick={() => {
                      setCreateAutoPw(!createAutoPw)
                      if (createAutoPw) setCreatePassword('')
                    }}
                    className="text-xs text-primary-400 hover:text-primary-300 underline"
                  >
                    {createAutoPw ? 'Set manually' : 'Auto-generate'}
                  </button>
                </div>
                {createAutoPw ? (
                  <div className="flex items-center gap-2">
                    <input
                      value={createPassword}
                      readOnly
                      className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2.5 text-gray-100 font-mono text-xs"
                    />
                    <button
                      type="button"
                      onClick={() => setCreatePassword(generatePassword())}
                      className="p-2.5 rounded-lg text-gray-400 hover:bg-gray-800 hover:text-gray-100"
                      title="Regenerate"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <input
                    type="password"
                    value={createPassword}
                    onChange={(e) => setCreatePassword(e.target.value)}
                    className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2.5 text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                )}
              </label>

              <label className="flex flex-col gap-1.5 text-sm">
                <span className="text-gray-400">Role</span>
                <select
                  value={createRole}
                  onChange={(e) => setCreateRole(e.target.value)}
                  className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2.5 text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setCreateOpen(false)}
                className="text-sm font-medium rounded-lg px-4 py-2 min-h-[44px] bg-gray-800/90 border border-gray-700 text-gray-200 hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={creating}
                onClick={() => void handleCreate()}
                className="inline-flex items-center gap-2 text-sm font-medium rounded-lg px-4 py-2 min-h-[44px] bg-primary-900/40 border border-primary-700/50 text-primary-100 hover:bg-primary-900/60 disabled:opacity-50"
              >
                {creating ? 'Creating…' : 'Create user'}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* ── Edit user modal ── */}
      {editTarget && (
        <ModalOverlay onClose={() => setEditTarget(null)}>
          <div className="p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-100">
                Edit <span className="text-primary-400">{editTarget.username}</span>
              </h2>
              <button
                type="button"
                onClick={() => setEditTarget(null)}
                className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-800 hover:text-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {editErr && (
              <div className="text-sm text-error-400 bg-red-950/30 border border-red-900/40 rounded-lg px-4 py-3">
                {editErr}
              </div>
            )}

            <div className="space-y-4">
              <label className="flex flex-col gap-1.5 text-sm">
                <span className="text-gray-400">Role</span>
                <select
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value)}
                  className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2.5 text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </label>

              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={editResetPw}
                  onChange={(e) => setEditResetPw(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-900 text-primary-500 focus:ring-primary-500"
                />
                <span className="text-gray-300">Reset password</span>
              </label>

              {editResetPw && (
                <label className="flex flex-col gap-1.5 text-sm">
                  <span className="text-gray-400">New password</span>
                  <input
                    type="password"
                    value={editPassword}
                    onChange={(e) => setEditPassword(e.target.value)}
                    className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2.5 text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter new password"
                  />
                </label>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setEditTarget(null)}
                className="text-sm font-medium rounded-lg px-4 py-2 min-h-[44px] bg-gray-800/90 border border-gray-700 text-gray-200 hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => void handleEditSave()}
                className="inline-flex items-center gap-2 text-sm font-medium rounded-lg px-4 py-2 min-h-[44px] bg-primary-900/40 border border-primary-700/50 text-primary-100 hover:bg-primary-900/60 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* ── Delete confirmation modal ── */}
      {deleteTarget && (
        <ModalOverlay onClose={() => setDeleteTarget(null)}>
          <div className="p-6 space-y-5">
            <div className="flex items-center gap-3">
              <Trash2 className="w-6 h-6 text-red-400" />
              <div>
                <h2 className="text-lg font-semibold text-gray-100">Delete user</h2>
                <p className="text-gray-400 text-sm mt-1">
                  Are you sure you want to delete <span className="font-semibold text-gray-200">{deleteTarget.username}</span>?
                  This action cannot be undone.
                </p>
              </div>
            </div>

            {deleteErr && (
              <div className="text-sm text-error-400 bg-red-950/30 border border-red-900/40 rounded-lg px-3 py-2">
                {deleteErr}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="text-sm font-medium rounded-lg px-4 py-2 min-h-[44px] bg-gray-800/90 border border-gray-700 text-gray-200 hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={() => void handleDelete()}
                className="inline-flex items-center gap-2 text-sm font-medium rounded-lg px-4 py-2 min-h-[44px] bg-red-900/40 border border-red-700/50 text-red-200 hover:bg-red-900/60 disabled:opacity-50"
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  )
}
